/**
 * NavBar — persistent bottom tab navigation.
 *
 * Tabs: Главная / Магазин / Заказы / Рефералы. Uses the hash router to highlight the
 * active section. There is no cart in the pilot — a product is ordered directly from its
 * page. Icons are inline line-SVGs matching the approved DESIGN/ bottom navbar.
 */
import { routes, useNavigate, useRoute } from "@shared/router";

import styles from "./NavBar.module.css";

type TabKey = "home" | "shop" | "orders" | "referrals";

interface Tab {
  readonly key: TabKey;
  readonly label: string;
  readonly icon: JSX.Element;
  readonly to: string;
}

const HomeIcon = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V20h14V9.5" />
  </svg>
);

const ShopIcon = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 2 3 7v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V7l-3-5z" />
    <path d="M3 7h18" />
    <path d="M16 11a4 4 0 0 1-8 0" />
  </svg>
);

const OrdersIcon = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1z" />
    <path d="M8 7h8" />
    <path d="M8 11h8" />
  </svg>
);

const ReferralsIcon = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13" />
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
  </svg>
);

const TABS: readonly Tab[] = [
  { key: "home", label: "Главная", icon: HomeIcon, to: routes.home() },
  { key: "shop", label: "Магазин", icon: ShopIcon, to: routes.shop() },
  { key: "orders", label: "Заказы", icon: OrdersIcon, to: routes.orders() },
  {
    key: "referrals",
    label: "Рефералы",
    icon: ReferralsIcon,
    to: routes.referrals(),
  },
];

/** Routes that take over the full screen (own bottom bar / chat) — hide the tab bar. */
const FULLSCREEN_ROUTES: ReadonlySet<string> = new Set([
  "order",
  "product",
  "checkout",
  "support-ticket",
]);

export function NavBar(): JSX.Element | null {
  const route = useRoute();
  const navigate = useNavigate();

  // Detail screens (esp. the order chat) are full-screen: no tab bar, no double scroll.
  if (FULLSCREEN_ROUTES.has(route.name)) {
    return null;
  }

  let active: TabKey = "home";
  if (route.name === "shop" || route.name === "product") {
    active = "shop";
  } else if (route.name === "orders" || route.name === "order") {
    active = "orders";
  } else if (route.name === "referrals") {
    active = "referrals";
  }

  return (
    <nav className={styles.nav} aria-label="Основная навигация">
      {TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={styles.tab}
          aria-current={active === tab.key ? "page" : undefined}
          data-active={active === tab.key}
          onClick={() => {
            navigate(tab.to);
          }}
        >
          <span className={styles.glyph}>{tab.icon}</span>
          <span className={styles.label}>{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}
